import React, { useEffect, useState } from "react";
import {
    Button,
    Dropdown,
    Icon,
    Label,
    Loader,
    Message,
    Pagination,
    Segment,
    Table,
} from "semantic-ui-react";
import { toast } from "react-toastify";
import { getDetailedOrderReport } from "../../../services/reporting.service";
import {
    DetailedOrder,
    DetailedOrderReportResponse,
    ReportFilters,
    SortDirection,
} from "../../../types/models/Reporting";
import OrderReportDetailModal from "../modals/OrderReportDetailModal";
import {
    formatCurrency,
    formatNumber,
    formatPercent,
    formatReportDate,
    orderStatusColor,
    orderStatusLabel,
    riskColor,
    riskLabel,
} from "../reportingFormatters";

interface Props {
    filters: ReportFilters;
    reloadToken: number;
    sortProperty: string;
    sortDirection: SortDirection;
    onSortChange: (property: string, direction: SortDirection) => void;
}

const DetailedOrdersReport = ({
    filters,
    reloadToken,
    sortProperty,
    sortDirection,
    onSortChange,
}: Props) => {
    const [data, setData] = useState<DetailedOrderReportResponse>();
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(25);
    const [selectedOrder, setSelectedOrder] = useState<DetailedOrder>();

    useEffect(() => {
        let mounted = true;
        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await getDetailedOrderReport(filters, {
                    page,
                    size,
                    sort: `${sortProperty},${sortDirection}`,
                });
                if (mounted) setData(response.data);
            } catch (error: any) {
                if (mounted) toast.error(error.message || "No fue posible consultar el reporte detallado.");
            } finally {
                if (mounted) setLoading(false);
            }
        };
        fetchData();
        return () => { mounted = false; };
    }, [filters, reloadToken, page, size, sortProperty, sortDirection]);

    useEffect(() => setPage(0), [filters, reloadToken]);

    const changeSort = (property: string) => {
        setPage(0);
        onSortChange(
            property,
            sortProperty === property && sortDirection === "asc" ? "desc" : "asc"
        );
    };

    const sorted = (property: string) => sortProperty === property
        ? (sortDirection === "asc" ? "ascending" : "descending")
        : undefined;

    const management = data?.managementSummary;

    return (
        <div className="dashboard-section">
            <div className="dashboard-section-heading">
                <div>
                    <span className="dashboard-eyebrow">Reporte operativo</span>
                    <h2>Cola operativa con contexto completo</h2>
                    <p>El periodo forma una cohorte por fecha de creación; la tabla evita datos personales innecesarios y abre el detalle bajo demanda.</p>
                </div>
                {filters.attentionOnly && (
                    <Label color="red" size="large">
                        <Icon name="warning sign" /> Sólo requieren atención
                    </Label>
                )}
            </div>

            {management && (
                <div className="report-summary-grid">
                    <div><span>Por atender</span><strong>{formatNumber(management.attentionOrders)}</strong></div>
                    <div><span>Críticos</span><strong>{formatNumber(management.criticalOrders)}</strong></div>
                    <div><span>Riesgo alto</span><strong>{formatNumber(management.highRiskOrders)}</strong></div>
                    <div><span>Etapas detenidas</span><strong>{formatNumber(management.stalledOrders)}</strong></div>
                    <div><span>Instalaciones vencidas</span><strong>{formatNumber(management.overdueInstallations)}</strong></div>
                    <div><span>Importe en riesgo</span><strong>{formatCurrency(management.amountAtRisk)}</strong></div>
                </div>
            )}

            <Segment>
                {loading && <Loader active inline="centered">Consultando réplica...</Loader>}
                {!loading && data?.orders.length === 0 && <Message info content="No existen pedidos para los filtros seleccionados." />}
                {!loading && Boolean(data?.orders.length) && (
                    <div className="dashboard-table-scroll">
                        <Table sortable striped compact selectable className="dashboard-table dashboard-report-table">
                            <Table.Header>
                                <Table.Row>
                                    <Table.HeaderCell sorted={sorted("riskScore")} onClick={() => changeSort("riskScore")}>Riesgo</Table.HeaderCell>
                                    <Table.HeaderCell sorted={sorted("invoice")} onClick={() => changeSort("invoice")}>Factura</Table.HeaderCell>
                                    <Table.HeaderCell sorted={sorted("createdAt")} onClick={() => changeSort("createdAt")}>Creación</Table.HeaderCell>
                                    <Table.HeaderCell sorted={sorted("clientName")} onClick={() => changeSort("clientName")}>Cliente</Table.HeaderCell>
                                    <Table.HeaderCell sorted={sorted("productName")} onClick={() => changeSort("productName")}>Producto</Table.HeaderCell>
                                    <Table.HeaderCell sorted={sorted("currentStatus")} onClick={() => changeSort("currentStatus")}>Estado</Table.HeaderCell>
                                    <Table.HeaderCell sorted={sorted("daysInCurrentStatus")} onClick={() => changeSort("daysInCurrentStatus")} textAlign="right">Días</Table.HeaderCell>
                                    <Table.HeaderCell sorted={sorted("advisorName")} onClick={() => changeSort("advisorName")}>Asesor</Table.HeaderCell>
                                    <Table.HeaderCell sorted={sorted("installerName")} onClick={() => changeSort("installerName")}>Instalador</Table.HeaderCell>
                                    <Table.HeaderCell sorted={sorted("totalAmount")} onClick={() => changeSort("totalAmount")} textAlign="right">Total</Table.HeaderCell>
                                    <Table.HeaderCell sorted={sorted("paidAmount")} onClick={() => changeSort("paidAmount")} textAlign="right">Pagado</Table.HeaderCell>
                                    <Table.HeaderCell sorted={sorted("outstandingBalance")} onClick={() => changeSort("outstandingBalance")} textAlign="right">Saldo</Table.HeaderCell>
                                    <Table.HeaderCell sorted={sorted("paymentProgressPercent")} onClick={() => changeSort("paymentProgressPercent")} textAlign="right">Cobranza</Table.HeaderCell>
                                    <Table.HeaderCell sorted={sorted("installationDate")} onClick={() => changeSort("installationDate")}>Instalación</Table.HeaderCell>
                                    <Table.HeaderCell sorted={sorted("openGuarantees")} onClick={() => changeSort("openGuarantees")} textAlign="right">Garantías</Table.HeaderCell>
                                    <Table.HeaderCell />
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {data?.orders.map((order) => (
                                    <Table.Row key={order.id} warning={order.management.requiresAttention}>
                                        <Table.Cell><Label color={riskColor(order.management.riskLevel)}>{riskLabel(order.management.riskLevel)} · {order.management.priorityScore}</Label></Table.Cell>
                                        <Table.Cell><strong>#{order.invoice || "—"}</strong></Table.Cell>
                                        <Table.Cell>{formatReportDate(order.createdAt)}</Table.Cell>
                                        <Table.Cell><strong>{order.client?.fullName || "Sin cliente"}</strong></Table.Cell>
                                        <Table.Cell><strong>{order.product?.name || "Sin producto"}</strong><small>{order.product?.sku || "Sin SKU"}</small></Table.Cell>
                                        <Table.Cell>
                                            <Label color={orderStatusColor(order.currentStatus?.code)}>
                                                {orderStatusLabel(order.currentStatus?.code, order.currentStatus?.label)}
                                            </Label>
                                        </Table.Cell>
                                        <Table.Cell textAlign="right">{formatNumber(order.management.daysInCurrentStatus)}</Table.Cell>
                                        <Table.Cell>{order.advisor?.fullName || "Sin asignar"}</Table.Cell>
                                        <Table.Cell>{order.installer?.fullName || "Sin asignar"}</Table.Cell>
                                        <Table.Cell textAlign="right">{formatCurrency(order.totalAmount)}</Table.Cell>
                                        <Table.Cell textAlign="right">{formatCurrency(order.paidAmount)}</Table.Cell>
                                        <Table.Cell textAlign="right"><strong>{formatCurrency(order.outstandingBalance)}</strong></Table.Cell>
                                        <Table.Cell textAlign="right">{formatPercent(order.management.paymentProgressPercent)}</Table.Cell>
                                        <Table.Cell>{formatReportDate(order.installationDate, true)}</Table.Cell>
                                        <Table.Cell textAlign="right">{formatNumber(order.management.openGuarantees)}</Table.Cell>
                                        <Table.Cell collapsing>
                                            <Button.Group size="mini">
                                                <Button secondary icon="info circle" onClick={() => setSelectedOrder(order)} />
                                                <Button
                                                    as="a"
                                                    secondary
                                                    icon="search"
                                                    href={`/orders/${order.id}/details`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                />
                                            </Button.Group>
                                        </Table.Cell>
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table>
                    </div>
                )}

                <div className="dashboard-pagination dashboard-pagination--split">
                    <span>{formatNumber(data?.totalElements)} pedidos</span>
                    <Pagination
                        activePage={page + 1}
                        totalPages={Math.max(data?.totalPages || 1, 1)}
                        onPageChange={(_, pagination) => setPage(Number(pagination.activePage) - 1)}
                    />
                    <Dropdown
                        compact
                        selection
                        value={size}
                        options={[10, 25, 50, 100].map((value) => ({ key: value, value, text: `${value} filas` }))}
                        onChange={(_, value) => { setPage(0); setSize(Number(value.value)); }}
                    />
                </div>
            </Segment>

            <OrderReportDetailModal order={selectedOrder} onClose={() => setSelectedOrder(undefined)} />
        </div>
    );
};

export default DetailedOrdersReport;
